import { Fragment, useState } from "react";

import { PencilIcon } from "@vericus/cadmus-icons";
import {
  Button,
  colors,
  IconButton,
  LinkButton,
  Skeleton,
  Text,
  Tooltip,
} from "@vericus/cadmus-ui";

import { PeerComment } from "@/features/feedback/components/peer-comment";
import { ThreadFragment } from "@/generated/graphql";

import {
  MEMBERS_COLUMN_LABEL,
  PeerOutcomeTable,
  STATUS_COLUMN_LABEL,
} from "../utils/utils";
import * as styles from "./peer-overview-table.css";

/**
 * The comment threads query has three distinct states the expanded row needs
 * to render differently — a plain `Map` can't tell "still loading" apart
 * from "loaded, no comment given".
 */
export type CommentThreadsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; threadsByUserId: Map<string, ThreadFragment> };

function hasVisibleText(html?: string | null): boolean {
  const text = new DOMParser().parseFromString(html ?? "", "text/html").body
    .textContent;
  return (text ?? "").trim() !== "";
}

interface Props {
  /** Criteria headers */
  headers: { id: string; title: string }[];
  /** Filter state for pending marks */
  filterByPending: boolean;
  /** Rows of the table */
  rows: PeerOutcomeTable;
  /** Group Member user IDs that have not yet been marked by the current student */
  pendingMarkIds: string[];
  /** Current student */
  me: { id: string; name: string };
  /** Hide per-row Edit button when peer eval is past due. */
  disableEdit?: boolean;
  /** Callback to trigger a Mark action on a row/student */
  onMarkStudent: (studentId: string) => void;
  /** Callback to trigger an Edit action on a completed row/student */
  onEditStudent: (studentId: string) => void;
  /** Comment thread the current student gave each peer (and themselves), keyed by userId */
  commentThreads?: CommentThreadsState;
}

/**
 * Table with Student peers as rows, and rubric Criteria as columns, with the
 * cells displaying the Criterion selection made by the current student marker.
 */
export function PeerOverviewTable({
  me,
  headers,
  rows,
  filterByPending,
  pendingMarkIds,
  disableEdit = false,
  onMarkStudent,
  onEditStudent,
  commentThreads,
}: Props) {
  const selfStyle = { background: colors.bgrey300 };

  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(
    new Set()
  );

  const toggleExpandedRow = (userId: string) => {
    setExpandedRowIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Member column + one column per criterion + status column.
  const columnCount = headers.length + 2;

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        {headers && (
          <thead>
            <tr>
              <th className={styles.tableHeader}>
                <Text kind="bodyLine" bold>
                  {MEMBERS_COLUMN_LABEL}
                </Text>
              </th>
              {headers.map((header, key) => (
                <th key={header.id} className={styles.tableHeader}>
                  <Text kind="headingSix">{`Criteria ${key + 1}`}</Text>
                  {header.title && <Text kind="bodyLine">{header.title}</Text>}
                </th>
              ))}
              <th className={styles.tableHeader}>
                <Text kind="bodyLine" bold>
                  {STATUS_COLUMN_LABEL}
                </Text>
              </th>
            </tr>
          </thead>
        )}

        <tbody>
          {rows.map((row) => {
            if (filterByPending && !pendingMarkIds.includes(row.userId)) {
              return null;
            }

            const isExpanded = expandedRowIds.has(row.userId);
            const thread =
              commentThreads?.status === "ready"
                ? commentThreads.threadsByUserId.get(row.userId)
                : undefined;
            const hasComment = hasVisibleText(thread?.comments?.[0]?.content);

            return (
              <Fragment key={`peer-row-${row.userId}`}>
                <tr>
                  <td
                    key={`column-student-student-${row.userId}`}
                    className={styles.column}
                    style={me.id === row.userId ? selfStyle : {}}
                  >
                    <Text kind="bodyLine" bold>
                      {me.id === row.userId ? `${row.userName} (Self Eval)` : row.userName}
                    </Text>
                  </td>
                  {row?.outcomes?.map((outcome) => (
                    <td
                      key={`column-${outcome.criteriaId}-student-${row.userId}`}
                      className={styles.column}
                      style={me.id === row.userId ? selfStyle : {}}
                    >
                      <Text kind="bodyLine">{outcome.scaleValue}</Text>
                    </td>
                  ))}
                  <td
                    key={`column-status-student-${row.userId}`}
                    className={styles.columnStatus}
                  >
                    {pendingMarkIds.includes(row.userId) ? (
                      <Button onClick={() => onMarkStudent(row.userId)}>
                        Mark
                      </Button>
                    ) : (
                      <div className={styles.statusCellContent}>
                        <Tooltip
                          content={
                            isExpanded
                              ? "Click to collapse comments section"
                              : "Click to expand comments section"
                          }
                        >
                          <LinkButton
                            type="button"
                            color="success"
                            underlined={false}
                            className={styles.doneLink}
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? "Hide" : "Show"} feedback given to ${row.userName}`}
                            onClick={() => toggleExpandedRow(row.userId)}
                          >
                            <Text kind="bodyLine" color="inherit" as="span">
                              Done
                            </Text>
                          </LinkButton>
                        </Tooltip>
                        {!disableEdit && (
                          <IconButton
                            shape="square"
                            outline
                            aria-label={`Edit evaluation for ${row.userName}`}
                            onClick={() => onEditStudent(row.userId)}
                          >
                            <PencilIcon label="" size={16} />
                          </IconButton>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {isExpanded && !pendingMarkIds.includes(row.userId) && (
                  <tr key={`peer-row-comment-${row.userId}`}>
                    <td colSpan={columnCount} className={styles.commentCell}>
                      {commentThreads?.status === "loading" && (
                        <Skeleton height={20} />
                      )}
                      {commentThreads?.status === "error" && (
                        <Text kind="bodyLine" color="red500">
                          Couldn&apos;t load feedback. Try again shortly.
                        </Text>
                      )}
                      {commentThreads?.status === "ready" &&
                        (hasComment ? (
                          <PeerComment thread={thread} />
                        ) : (
                          <Text kind="bodyLine" italic>
                            No comments were given for this evaluation
                          </Text>
                        ))}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
